/**
 * The roles a person can hold inside a tutoring organisation.
 *
 * This is the one place the set is defined, because it is read from three
 * sides that must agree: the server stamps a role into the LiveKit token, the
 * call UI puts it on participant tiles, and the dashboard assigns it. Adding
 * a role means adding a row here and a badge colour in CSS.
 *
 * Colour lives in CSS rather than in this table on purpose — the same role
 * has to read correctly on the light dashboard and inside the dark call
 * window, which is a styling decision, not a data one.
 */
export type MemberRoleSlug = 'teacher' | 'curator' | 'student';

export interface MemberRole {
  slug: MemberRoleSlug;
  label: string;
}

/** Ordered by standing, which is the order lists and pickers show them in. */
export const MEMBER_ROLES: readonly MemberRole[] = [
  { slug: 'teacher', label: 'Учитель' },
  { slug: 'curator', label: 'Куратор' },
  { slug: 'student', label: 'Ученик' },
];

export function isMemberRoleSlug(value: unknown): value is MemberRoleSlug {
  return MEMBER_ROLES.some((role) => role.slug === value);
}

/** The role for a slug, or null for the many people who simply have none. */
export function memberRole(slug: string | null | undefined): MemberRole | null {
  if (!slug) return null;
  return MEMBER_ROLES.find((role) => role.slug === slug) ?? null;
}

export function memberRoleLabel(slug: string | null | undefined): string | null {
  return memberRole(slug)?.label ?? null;
}
